/**
 * TachoChart — 24-hour linear activity timeline.
 *
 * Renders a single day as a horizontal strip where each minute
 * is coloured by activity type, matching EU digital tachograph
 * printout conventions:
 *
 *   Driving      — blue  (thickest band)
 *   Work         — amber
 *   Availability — violet
 *   Rest         — emerald (thinnest band)
 *
 * Hour grid lines and optional compliance markers are overlaid.
 */

import { ActivityRecord } from '../types'

const ACTIVITY_COLORS: Record<string, string> = {
  driving: '#3b82f6',
  work: '#f59e0b',
  availability: '#8b5cf6',
  rest: '#10b981',
}

const ACTIVITY_HEIGHTS: Record<string, number> = {
  driving: 1.0,
  work: 0.72,
  availability: 0.48,
  rest: 0.28,
}

interface Props {
  activities: ActivityRecord[]
  date: string
  height?: number
  showHourLabels?: boolean
  showVehicle?: string
  compact?: boolean
}

export function TachoChart({
  activities,
  date,
  height = 40,
  showHourLabels = false,
  showVehicle,
  compact = false,
}: Props) {
  const dayActivities = activities
    .filter((a) => a.date === date)
    .sort((a, b) => (a.start_time || '').localeCompare(b.start_time || ''))

  const totalMinutes = 1440

  return (
    <div className="relative" style={{ height }}>
      {/* Background */}
      <div className="absolute inset-0 bg-gray-100 rounded-sm overflow-hidden">
        {/* Activity blocks */}
        {dayActivities.map((act, i) => {
          const startMin = act.time_minutes ?? getMinutes(act.start_time)
          const duration = act.duration_minutes || 0
          if (duration <= 0) return null

          const left = (startMin / totalMinutes) * 100
          const width = (duration / totalMinutes) * 100
          const color = ACTIVITY_COLORS[act.activity_type] || '#9ca3af'
          const heightPct = ACTIVITY_HEIGHTS[act.activity_type] ?? 0.5

          return (
            <div
              key={i}
              className="absolute bottom-0"
              style={{
                left: `${left}%`,
                width: `${Math.max(width, 0.15)}%`,
                height: `${heightPct * 100}%`,
                backgroundColor: color,
              }}
              title={`${act.activity_type}: ${formatTime(startMin)} – ${formatTime(startMin + duration)} (${duration}min)`}
            />
          )
        })}

        {/* Hour grid lines */}
        {!compact &&
          Array.from({ length: 24 }, (_, h) => (
            <div
              key={h}
              className="absolute top-0 bottom-0"
              style={{
                left: `${(h / 24) * 100}%`,
                width: '1px',
                backgroundColor: h % 6 === 0 ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.07)',
              }}
            />
          ))}

        {/* 4.5h continuous driving marker (270 min) */}
        {!compact && (
          <div
            className="absolute top-0 bottom-0 border-l border-dashed border-red-300"
            style={{ left: `${(270 / totalMinutes) * 100}%` }}
            title="4.5h continuous driving limit"
          />
        )}
      </div>

      {/* Hour labels beneath */}
      {showHourLabels && (
        <div className="absolute -bottom-4 left-0 right-0 flex justify-between">
          {[0, 3, 6, 9, 12, 15, 18, 21, 24].map((h) => (
            <span
              key={h}
              className="text-gray-400"
              style={{ fontSize: '9px', transform: 'translateX(-50%)', position: 'absolute', left: `${(h / 24) * 100}%` }}
            >
              {String(h).padStart(2, '0')}
            </span>
          ))}
        </div>
      )}

      {/* Vehicle overlay label */}
      {showVehicle && (
        <span className="absolute top-0.5 right-1 text-white text-xs font-bold opacity-70 pointer-events-none" style={{ fontSize: '8px' }}>
          {showVehicle}
        </span>
      )}
    </div>
  )
}

function getMinutes(isoStr?: string): number {
  if (!isoStr) return 0
  const d = new Date(isoStr)
  return d.getUTCHours() * 60 + d.getUTCMinutes()
}

function formatTime(mins: number): string {
  const h = Math.floor(mins / 60) % 24
  const m = mins % 60
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
}

// Also export the time_minutes getter in case ActivityRecord doesn't have it
interface ActivityRecordExt extends ActivityRecord {
  time_minutes?: number
}
